'use client';

import { useEffect, useRef, useState } from 'react';
import { CookieConsent } from '@/app/_components/CookieConsent';
import { ThemePreferenceSync } from '@/app/_components/ThemePreferenceSync';
import { AnalyticsIdentityBridge } from '@/app/_components/AnalyticsIdentityBridge';
import { NotificationToastBridge } from '@/app/_components/NotificationToastBridge';
import { SearchSheetMount } from '@/app/_components/SearchSheetMount';
import { ThemeCookieSync } from '@/app/_components/ThemeCookieSync';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { AnalyticsProvider } from '@/contexts/AnalyticsProvider';
// Deep import (not the locus barrel) so the root client chunk carries only
// the gate — the runtime itself lazy-loads behind the mount's three gates.
import { LocusGlobalMount } from '@/components/overlay/locus/LocusGlobalMount';
import { StoreProvider, setUnauthorizedHandler } from '@loci/store';
import { useGetCurrentUserQuery } from '@loci/api';
import { ThemeProvider, ToastContainer } from '@loci/ui';
import {
  consumePendingAuthMethod,
  resolveIsNewUser,
  trackAuthCompleted,
} from '@/lib/analytics/auth';
import { clearOAuthReturnIndicator, hasSessionHint, isOAuthReturn } from '@/lib/auth';
import { reconcileSessionVersion } from '@/lib/auth/sessionVersion';
import { handleUnauthorized, isCityScopedAppSurface } from '@/lib/auth/sessionSanitize';
import { useJustLinkedToast } from '@/hooks/useJustLinkedToast';
import { useCitySurface } from '@/lib/city-context.client';
import type { SsrThemeSource, ThemeMode } from '@/lib/theme/ssrTheme';
import { useResolvedSsrThemeMode } from '@/lib/theme/useResolvedSsrThemeMode';
import { installHonestEmptyTicketsQuery } from '@/lib/profile/installHonestEmptyTicketsQuery';

installHonestEmptyTicketsQuery();

interface AppInitializerProps {
  children: React.ReactNode;
}

/**
 * AppInitializer bootstraps the user session at app startup.
 *
 * Two entry paths trigger bootstrap:
 *
 * 1. **OAuth return (FRE-683):** when the current URL carries the
 *    `OAUTH_RETURN_PARAM` indicator, the backend has just set httpOnly auth
 *    cookies via `/api/v1/auth/oauth/<provider>/callback`. The session hint
 *    may be absent (first-time sign-in), so we force `bootstrapSession()` to
 *    bypass the hint gate and fetch `/auth/me`. The indicator is then stripped
 *    from the URL via `history.replaceState` so a reload does not re-trigger.
 *
 * 2. **Existing session:** if a session hint is present in localStorage, we
 *    issue a single `/auth/me` fetch to hydrate the RTK Query cache.
 *
 * Anonymous visitors (no hint, no OAuth indicator) do **not** trigger any
 * auth request — this avoids a guaranteed 401 on every public page. 401
 * responses on authenticated bootstraps are treated as "not logged in"
 * without error UI.
 */
function AppInitializer({ children }: AppInitializerProps) {
  const { bootstrapSession, logout, user } = useAuth();
  const pendingOAuthMethodRef = useRef<ReturnType<typeof consumePendingAuthMethod>>(null);
  const oauthCompletionFiredRef = useRef(false);
  // Read the session hint after mount to avoid SSR hydration mismatch.
  const [shouldFetchUser, setShouldFetchUser] = useState(false);
  // Latch the session-version wipe so a logout-triggered re-render doesn't
  // re-enter the effect and double-fire the logout mutation.
  const sessionResetFiredRef = useRef(false);

  // Reconcile the client-side session-version stamp before deciding whether
  // to bootstrap. When the stamp drifts (a deploy that changed auth or
  // onboarding semantics), wipe session-scoped localStorage and trigger
  // the full logout flow so the user lands cleanly on the login surface
  // instead of trying to hydrate a session that no longer means what it
  // used to. `reconcileSessionVersion()` is SSR-safe; this effect still
  // gates it on mount because `logout()` requires a browser context.
  useEffect(() => {
    if (sessionResetFiredRef.current) return;
    const result = reconcileSessionVersion();
    if (result.status !== 'wiped') return;
    sessionResetFiredRef.current = true;
    // `logout()` clears auth cookies via Set-Cookie (works on expired/stale
    // tokens too), drops the session hint, and hard-navigates to the
    // app-host login surface. We swallow rejections because the navigation
    // itself happens in the catch branch as well.
    void logout().catch(() => undefined);
  }, [logout]);

  // Register the central unauthorized (401) handler so any authenticated RTK
  // Query that the server rejects funnels into `sanitizeInvalidSession()`
  // (clear hint → evict cookies → /login?force=1 on the app surface). This is
  // the most reliable trigger — it fires on the exact server rejection, on any
  // endpoint, covering cookie expiry / secret rotation / server-side revocation
  // that a realtime push can't (a closed tab never receives the push). The
  // handler is itself app-surface-gated, so anonymous marketing browsing is
  // never force-redirected.
  useEffect(() => {
    setUnauthorizedHandler(handleUnauthorized);
    return () => setUnauthorizedHandler(null);
  }, []);

  // OAuth return handling runs first so the bootstrap query picks up the
  // newly-set cookies on next paint without waiting for the hint check.
  useEffect(() => {
    if (!isOAuthReturn()) return;
    pendingOAuthMethodRef.current = consumePendingAuthMethod();
    bootstrapSession();
    clearOAuthReturnIndicator();
  }, [bootstrapSession]);

  // WEB-1212: OAuth has no response body on the return URL. Fire
  // `auth_completed` once `/auth/me` resolves the account. `is_new_user`
  // prefers an explicit flag and otherwise a `createdAt` within two minutes.
  useEffect(() => {
    const method = pendingOAuthMethodRef.current;
    if (!method || !user || oauthCompletionFiredRef.current) return;
    oauthCompletionFiredRef.current = true;
    pendingOAuthMethodRef.current = null;
    trackAuthCompleted({
      method,
      isNewUser: resolveIsNewUser(user),
      accountEmail: user.email,
    });
  }, [user]);

  useEffect(() => {
    setShouldFetchUser(hasSessionHint());
  }, []);

  // Layer-2 stuck-state detection. proxy.ts admits a request to a *city-scoped*
  // app path (`app.loci.city/<city>/…`) only when an auth cookie is present, so
  // reaching one with NO session hint means the cookie is present-but-stale
  // (e.g. the prior recovery cleared the hint without evicting the cookie).
  // Without the hint, `getCurrentUser` is skipped and every other authed query
  // is gated on the null user, so no 401 ever fires and the user is silently
  // trapped. Force the bootstrap so `/auth/me` runs, resolves unauthenticated,
  // and the recovery effect funnels into `sanitizeInvalidSession()`.
  //
  // Gated to `isCityScopedAppSurface()`, NOT the host alone: city-less app
  // paths (`/login`, `/signup`, `/admin`, `/coming-soon`) are admitted to the
  // app host anonymous (proxy gate requires `cityCode`), so a host-only gate
  // would force-bootstrap a signed-out visitor on `/login`, trip the recovery,
  // and bounce them through `/login?force=1` forever. Skipped after a
  // version-drift wipe (which already runs the full logout flow).
  useEffect(() => {
    if (sessionResetFiredRef.current) return;
    if (hasSessionHint()) return;
    if (!isCityScopedAppSurface()) return;
    bootstrapSession();
  }, [bootstrapSession]);

  useGetCurrentUserQuery(undefined, {
    skip: !shouldFetchUser,
    // WEB-810: reuse the settled `/auth/me` cache; do not start a second cycle.
    refetchOnMountOrArgChange: false,
  });

  // FRE-696: surface a one-shot toast when the backend auto-linked a new
  // OAuth identity to an existing LOCI account. `skip` preserves the
  // anonymous-visitor guard on `/auth/me`.
  useJustLinkedToast({ skip: !shouldFetchUser });

  return <>{children}</>;
}

interface ProvidersProps {
  children: React.ReactNode;
  /**
   * Theme resolved in the root server layout (cookie / forced / brand default).
   * Passed through as ThemeProvider `mode` so hydration cannot pick a
   * different value (WEB-693 / O-7).
   */
  ssrTheme?: ThemeMode;
  themeSource?: SsrThemeSource;
}

export function Providers({
  children,
  ssrTheme = 'dark',
  themeSource = 'default',
}: ProvidersProps) {
  // WEB-612: production `marketing-city` is forced-dark (matches the SSR
  // `data-theme` pin on `<html>` in `app/layout.tsx`). Disable persistence so
  // a visitor's light preference on `app` cannot leak onto city hosts, and so
  // city dark does not overwrite the app preference.
  // WEB-694 / O-7: the durable preference cache is the `loci-theme` cookie
  // (`light` | `dark` | `system`, owned by ThemePreferenceSync). WEB-693
  // reads the resolved `light` | `dark` value for SSR `data-theme`.
  // `storageKey={null}` on every surface; `followSystem={false}` so this
  // provider does not fight the preference store's matchMedia listener.
  // Intentionally NOT applied to `fallback` — that surface also serves the
  // themable `(app)` shell on localhost / preview hosts.
  const surface = useCitySurface();
  const forcedDarkCitySurface = surface === 'marketing-city';
  const source: SsrThemeSource = forcedDarkCitySurface ? 'forced' : themeSource;
  const [mode, setMode] = useResolvedSsrThemeMode({
    source,
    ssrTheme: forcedDarkCitySurface ? 'dark' : ssrTheme,
  });
  const committedMode: ThemeMode = source === 'forced' ? 'dark' : mode;

  return (
    <StoreProvider>
      <ThemeProvider mode={committedMode} storageKey={null} followSystem={false} applyCssVariables>
        <ThemeCookieSync source={source} committedMode={committedMode} onResolved={setMode} />
        <AnalyticsProvider>
          <AuthProvider>
            <AppInitializer>
              {children}
              {/* Global notifications container (uses Redux store) */}
              <ToastContainer />
              {/* WEB-435: SINGLE canonical mount of the userNotifications
                  subscription. Bridges high-severity frames to the toast queue
                  above. The bell/panel deliberately do NOT mount the hook. */}
              <NotificationToastBridge />
              {/* FRE-1261: SINGLE mount aliasing the auth session to the
                  PostHog person identity so feature flags bucket by user,
                  not device UUID. Consent-gated inside lib/analytics. */}
              <AnalyticsIdentityBridge />
              {/* WEB-694: apply the stored Light/Dark/System preference and
                  track prefers-color-scheme while System is selected. */}
              <ThemePreferenceSync forcedDark={source === 'forced'} />
              <CookieConsent />
              {/* FRE-816: mount the global SearchSheet exactly once at the
                  highest shared client boundary so both consumer (AppHeader)
                  and marketing (SiteHeader) surfaces can trigger it via
                  `useSearchSheet().setOpen(true)`. WEB-220 built the
                  organism but no caller ever rendered it. */}
              <SearchSheetMount />
              {/* FRE-1229: SINGLE global Locus mount (PRD §6.3). Triple-gated —
                  `locus-runtime` PostHog flag (default-off, FRE-1231's rollout
                  lever), signed-in session (PRD §6.4), and the route-group
                  surface matrix (`(admin)` never mounts). Renders null and
                  downloads nothing until every gate passes. */}
              <LocusGlobalMount />
            </AppInitializer>
          </AuthProvider>
        </AnalyticsProvider>
      </ThemeProvider>
    </StoreProvider>
  );
}
